/** @jsxImportSource react */
import LookupInput from './LookupInput'
import { formatValue, rollupSummary } from './fieldValues'
import PersonInput from './PersonInput'
import {
  Autocomplete, Checkbox, Chip, FormControl, FormControlLabel, FormHelperText, FormLabel,
  InputAdornment, MenuItem, Radio, RadioGroup, Switch, TextField,
} from '@mui/material'

const TEXT_INPUT_TYPES = { EMAIL: 'email', PHONE: 'tel', URL: 'url', DATE: 'date', DATETIME: 'datetime-local' }

/** One input for one form field; value shapes match fieldValues.js. */
const FieldInput = ({ field, value, onChange, error, disabled, lookupRef }) => {
  const label = field.label
  const helper = error || field.helpText
  const isDisabled = disabled || field.readOnly
  const common = { label, required: field.required, disabled: isDisabled, error: !!error, helperText: helper, placeholder: field.placeholder || undefined }

  switch (field.fieldType) {
    case 'TEXTAREA':
      return <TextField {...common} multiline minRows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} inputProps={{ maxLength: field.maxLength || undefined }} />

    case 'NUMBER':
    case 'DECIMAL':
    case 'CURRENCY':
      return (
        <TextField
          {...common}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ inputMode: field.fieldType === 'NUMBER' ? 'numeric' : 'decimal' }}
          InputProps={field.fieldType === 'CURRENCY' ? { startAdornment: <InputAdornment position="start">¤</InputAdornment> } : undefined}
        />
      )

    case 'ROLLUP':
      // calculated on the server from the records linking here
      return (
        <TextField {...common} required={false} disabled value={value === undefined || value === null ? '' : formatValue(field, value)}
          placeholder="—" InputLabelProps={{ shrink: true }}
          helperText={helper || `Calculated: ${rollupSummary(field)}`} />
      )

    case 'DATE':
    case 'DATETIME':
      return (
        <TextField
          {...common}
          type={TEXT_INPUT_TYPES[field.fieldType]}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={field.fieldType === 'DATETIME' ? { step: 1 } : undefined}
        />
      )

    case 'CHECKBOX':
      return (
        <FormControl error={!!error} disabled={isDisabled}>
          <FormControlLabel control={<Switch checked={!!value} onChange={(e) => onChange(e.target.checked)} />} label={label} />
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      )

    case 'SELECT':
      return (
        <TextField {...common} select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          {!field.required && <MenuItem value=""><em>None</em></MenuItem>}
          {field.options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      )

    case 'RADIO':
      return (
        <FormControl error={!!error} disabled={isDisabled} required={field.required}>
          <FormLabel sx={{ fontSize: 13 }}>{label}</FormLabel>
          <RadioGroup row value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
            {field.options.map((o) => <FormControlLabel key={o.value} value={o.value} control={<Radio size="small" />} label={o.label} />)}
          </RadioGroup>
          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
      )

    case 'USER':
      return (
        <PersonInput value={value} onChange={onChange} initialRef={lookupRef} label={label}
          required={field.required} error={error} helperText={field.helpText} disabled={isDisabled} />
      )

    case 'LOOKUP':
      return (
        <LookupInput field={field} value={value} onChange={onChange} initialRef={lookupRef}
          required={field.required} error={error} helperText={field.helpText} disabled={isDisabled} />
      )

    case 'MULTI_SELECT': {
      const selected = Array.isArray(value) ? value : []
      return (
        <Autocomplete
          multiple
          disableCloseOnSelect
          disabled={isDisabled}
          options={field.options.map((o) => o.value)}
          getOptionLabel={(v) => field.options.find((o) => o.value === v)?.label ?? v}
          value={selected}
          onChange={(_, next) => onChange(next)}
          renderOption={(props, option, { selected: isSelected }) => (
            <li {...props} key={option}>
              <Checkbox size="small" checked={isSelected} sx={{ mr: 1, p: 0.25 }} />
              {field.options.find((o) => o.value === option)?.label ?? option}
            </li>
          )}
          renderTags={(tags, getTagProps) =>
            tags.map((tag, index) => {
              const { key, ...tagProps } = getTagProps({ index })
              return <Chip key={key} {...tagProps} label={field.options.find((o) => o.value === tag)?.label ?? tag} />
            })
          }
          renderInput={(params) => <TextField {...params} label={label} required={field.required && selected.length === 0} error={!!error} helperText={helper} />}
        />
      )
    }

    default:
      return (
        <TextField
          {...common}
          type={TEXT_INPUT_TYPES[field.fieldType] || 'text'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ maxLength: field.maxLength || undefined }}
        />
      )
  }
}

export default FieldInput
